// CreateTemplateExperience.tsx — the create-template dialog.
//
// Replaces the old braces-based dialog with a brace-free flow:
//   Step 1 — pick a ready-made template (personalizations already placed) or start blank
//   Step 2 — edit the words, personalize, submit for WhatsApp approval
//
// The customer never sees "{{ }}"; personalizations are inserted as pills and
// serialised to the EXACT payload the existing backend already accepts, so the
// backend is unchanged. The original CreateTemplateDialog.tsx is left untouched (its
// usage is commented out in index.jsx).

import { useCallback, useMemo, useRef, useState } from 'react'
import { message } from 'antd'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Dialog, DialogTitle } from 'V2.0/common/dialog'
import * as whatsappService from 'V2.0/services/whatsappService'
import {
  TemplateDraft,
  TemplateDraftController,
  Segment,
  FieldDef,
  MetaError,
  Starter,
  STARTERS,
  emptyDraft,
  loadStarter,
  defaultCatalog,
  makeCustomField,
  buildPayload,
  validateDraft,
  segmentsToText
} from './templateModel'
import {
  WhatsAppPreview,
  TemplateBasics,
  MessageComposer,
  ButtonsEditor,
  PersonalizationDetails,
  VariableStyleInline,
  CreateActions
} from './TemplateUI'

/* ------------------------------ draft state ------------------------------ */

function useTemplateDraft(projectId: string): TemplateDraftController {
  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft())
  const [catalog, setCatalog] = useState<FieldDef[]>(defaultCatalog())
  const catalogRef = useRef<FieldDef[]>(catalog)
  catalogRef.current = catalog

  const [submitting, setSubmitting] = useState(false)
  const [metaError, setMetaError] = useState<MetaError | null>(null)

  const patch = useCallback((p: Partial<TemplateDraft>) => setDraft(d => ({ ...d, ...p })), [])
  const setBody = useCallback(
    (bodySegments: Segment[]) => setDraft(d => ({ ...d, bodySegments })),
    []
  )
  const reset = useCallback((next?: TemplateDraft) => setDraft(next || emptyDraft()), [])

  // Register (or reuse) a custom detail and return it so the editor can insert it now.
  const registerCustom = useCallback((label: string): FieldDef => {
    const def = makeCustomField(label)
    const existing = catalogRef.current.find(f => f.key === def.key)
    if (existing) return existing
    const next = [...catalogRef.current, def]
    catalogRef.current = next
    setCatalog(next)
    return def
  }, [])

  const validation = useMemo(() => validateDraft(draft), [draft])

  const submit = useCallback(async (): Promise<boolean> => {
    const result = validateDraft(draft)
    if (!result.ok) {
      message.error(Object.values(result.errors)[0] || 'Please complete the template')
      return false
    }
    try {
      setSubmitting(true)
      setMetaError(null)
      await whatsappService.createTemplate(projectId, buildPayload(draft))
      message.success('Template created and sent for approval')
      return true
    } catch (error: any) {
      if (error?.response?.data?.error) {
        const e = error.response.data.error
        setMetaError({
          title: e.error_user_title || 'Template needs a tweak',
          message:
            e.error_user_msg ||
            e.message ||
            'WhatsApp could not accept this template. Please adjust and try again.'
        })
      } else {
        message.error('Failed to create template')
      }
      return false
    } finally {
      setSubmitting(false)
    }
  }, [draft, projectId])

  return {
    draft,
    patch,
    setBody,
    reset,
    catalog,
    registerCustom,
    validation,
    submitting,
    submit,
    metaError,
    setMetaError
  }
}

/* ------------------------------- the dialog ------------------------------- */

interface Props {
  projectId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function CreateTemplateExperience({ projectId, isOpen, onClose, onSuccess }: Props) {
  const ctrl = useTemplateDraft(projectId)
  const [editing, setEditing] = useState(false)

  const pickStarter = (starter: Starter) => {
    ctrl.reset(loadStarter(starter))
    setEditing(true)
  }
  const startBlank = () => {
    ctrl.reset(emptyDraft())
    setEditing(true)
  }
  const submit = async () => {
    if (await ctrl.submit()) {
      onSuccess?.()
      onClose()
    }
  }

  const hasVars = ctrl.draft.bodySegments.some(s => s.type === 'field')

  return (
    <Dialog open={isOpen} onClose={onClose} size="5xl">
      <DialogTitle>Create WhatsApp Template</DialogTitle>

      <div className="mt-5 max-h-[68vh] overflow-y-auto pr-1">
        {!editing ? (
          /* ---------- Step 1: pick a starting point ---------- */
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Pick a starting point — you can change every word.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {STARTERS.map(s => (
                <StarterCard key={s.id} starter={s} onClick={() => pickStarter(s)} />
              ))}
              <button
                type="button"
                onClick={startBlank}
                className="flex min-h-[132px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 p-4 text-sm font-medium text-gray-500 hover:border-green-300 hover:text-green-600"
              >
                <span className="mb-1 text-2xl">✏️</span>
                Start from scratch
              </button>
            </div>
          </div>
        ) : (
          /* ---------- Step 2: creation page ---------- */
          <div className="space-y-5">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800"
            >
              <ArrowLeftIcon className="size-4" />
              Back to templates
            </button>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]">
              <div className="space-y-4">
                <TemplateBasics
                  draft={ctrl.draft}
                  patch={ctrl.patch}
                  errors={ctrl.validation.errors}
                />
                <MessageComposer ctrl={ctrl} />
                <ButtonsEditor
                  draft={ctrl.draft}
                  patch={ctrl.patch}
                  errors={ctrl.validation.errors}
                />
                {/* Quiet, de-emphasised — present for those who need it, effectively
                    invisible to everyone else. */}
                {hasVars && (
                  <VariableStyleInline
                    value={ctrl.draft.parameterFormat}
                    onChange={f => ctrl.patch({ parameterFormat: f })}
                  />
                )}
              </div>
              <div className="self-start lg:sticky lg:top-2">
                <WhatsAppPreview draft={ctrl.draft} />
                {/* Spells out the actual variable names, so the user knows what to
                    name their sheet columns. */}
                <PersonalizationDetails draft={ctrl.draft} />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <CreateActions
                onCancel={onClose}
                onSubmit={submit}
                submitting={ctrl.submitting}
                disabled={!ctrl.validation.ok}
              />
            </div>
          </div>
        )}
      </div>

      {/* Meta rejection / API error */}
      {ctrl.metaError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="font-semibold text-gray-900">{ctrl.metaError.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{ctrl.metaError.message}</p>
            <button
              onClick={() => ctrl.setMetaError(null)}
              className="mt-6 w-full rounded bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Got it, I’ll fix it
            </button>
          </div>
        </div>
      )}
    </Dialog>
  )
}

function StarterCard({ starter, onClick }: { starter: Starter; onClick: () => void }) {
  const preview = segmentsToText(loadStarter(starter).bodySegments, 'preview')
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col rounded-xl border border-gray-200 p-4 text-left transition hover:border-green-400 hover:shadow-sm"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xl">{starter.emoji}</span>
        <span className="text-sm font-semibold text-gray-900">{starter.title}</span>
      </div>
      <p className="mb-2 line-clamp-2 text-xs text-gray-500">{preview}</p>
      <span className="mt-auto inline-block w-fit rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
        {starter.category}
      </span>
    </button>
  )
}
